import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import { useQueries } from '@tanstack/react-query'

import { getWallet } from '../api/portal'
import type { Membership } from './session'
import { StatusChip } from '../components/ui/StatusChip'
import { formatIrr } from '../format'
import { roleLabel } from '../vocabulary'

/** Two letters of the account name, matching the sidebar's identity mark. */
function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
}

/**
 * Which account you are about to act for.
 *
 * This screen predates the service portal and still looked it: a stack of
 * outlined buttons with the role in the corner, which is a form control asking
 * a question that is really a choice between two businesses. It is the very
 * first thing a customer with more than one account sees, so it was also the
 * one screen that did not look like the product behind it.
 *
 * Each account carries its balance, fetched per account, because that is what
 * distinguishes two rows that otherwise differ by a word. A role that cannot
 * read the wallet simply has no figure — the request is refused and the line
 * is left out rather than shown as zero.
 *
 * The chooser appears only when there is a genuine choice. One membership is
 * resolved without asking, and a persisted selection that still matches a live
 * membership restores without asking; this is what remains when neither is
 * true, and none of it is authorisation — every request the portal makes is
 * checked against the membership on the server regardless of what was clicked
 * here.
 */
export function AccountChooser({
  memberships,
  onChoose,
}: {
  memberships: Membership[]
  onChoose: (customerId: string) => void
}) {
  const wallets = useQueries({
    queries: memberships.map((row) => ({
      queryKey: ['client', 'wallet', row.customer.id],
      queryFn: ({ signal }: { signal: AbortSignal }) => getWallet(row.customer.id, signal),
      // A role without the wallet capability is refused, which is a correct
      // answer rather than a failure worth retrying.
      retry: false,
      staleTime: 30_000,
    })),
  })

  return (
    <Box
      sx={{
        display: 'grid',
        placeItems: 'center',
        minHeight: '100vh',
        p: { xs: 2, sm: 3 },
        bgcolor: 'background.default',
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 460 }}>
        <Typography variant="h1" gutterBottom>
          انتخاب حساب
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          شما به بیش از یک حساب دسترسی دارید. آنچه در ادامه می‌بینید مربوط به حسابی است که اینجا
          انتخاب می‌کنید.
        </Typography>

        <Stack spacing={1.5}>
          {memberships.map((row, index) => {
            // Guarded, not trusted. `formatIrr` throws on anything that is not
            // an integer — correct everywhere else, fatal here: this is the one
            // screen standing between a customer and the whole portal, and a
            // malformed balance must cost them a figure rather than the door.
            const balance = wallets[index]?.data?.available_balance_irr
            const money = Number.isInteger(balance) ? formatIrr(balance as number) : null
            return (
              <Paper key={row.customer.id} variant="outlined" sx={{ overflow: 'hidden' }}>
                <ButtonBase
                  onClick={() => onChoose(row.customer.id)}
                  sx={{
                    width: '100%',
                    // A comfortable target on a phone, where this is most
                    // likely to be read.
                    p: 2,
                    minHeight: 76,
                    textAlign: 'start',
                    justifyContent: 'flex-start',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1.75}
                    sx={{ alignItems: 'center', width: '100%', minWidth: 0 }}
                  >
                    <Avatar
                      variant="rounded"
                      sx={{
                        width: 44,
                        height: 44,
                        bgcolor: 'primary.main',
                        fontSize: 15,
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {initials(row.customer.name)}
                    </Avatar>

                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>
                        {row.customer.name}
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ alignItems: 'center', mt: 0.25, minWidth: 0 }}
                      >
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {roleLabel(row.role)}
                        </Typography>
                        {money ? (
                          <>
                            <Typography variant="caption" color="text.secondary">
                              ·
                            </Typography>
                            <Typography
                              variant="caption"
                              noWrap
                              sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}
                            >
                              {money}
                            </Typography>
                          </>
                        ) : null}
                      </Stack>
                    </Box>

                    {row.customer.status === 'active' ? null : (
                      <Box sx={{ flexShrink: 0 }}>
                        <StatusChip status={row.customer.status} />
                      </Box>
                    )}
                    <ChevronLeftIcon fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0 }} />
                  </Stack>
                </ButtonBase>
              </Paper>
            )
          })}
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
          دسترسی به حساب‌ها را گردانندهٔ سامانه تعیین می‌کند. اگر حسابی را اینجا نمی‌بینید، با
          پشتیبانی تماس بگیرید.
        </Typography>
      </Box>
    </Box>
  )
}
